import React from "react";
import { motion } from "framer-motion";
import { FiCheckCircle } from "react-icons/fi";
import SectionHeading from "../../components/SectionHeading";
import CertificationLogos from "../../components/CertificationLogos";
import { servicesContent } from "./foodSafetyContent";

const cardVariants = {
  hidden: { opacity: 0, y: 40 },
  visible: { opacity: 1, y: 0, transition: { duration: 0.45, ease: "easeOut" } },
};

/* One card per food safety service, each led by that standard's real mark from
   the shared registry. All five have a logo file, so no fallback is reached.
   The two lists are kept visually distinct — the scope list on ticks, the
   benefits on a tinted panel — so a card of two long lists still scans. */
const FoodSafetyStandards = () => (
  <section
    className="bg-gray-50 py-14 lg:py-20"
    aria-labelledby="food-safety-services-heading"
  >
    <div className="brand-col">
      <SectionHeading
        id="food-safety-services-heading"
        title={servicesContent.title}
      />
      <motion.ul
        className="grid list-none grid-cols-1 gap-6 p-0 lg:grid-cols-2 lg:gap-8"
        initial="hidden"
        whileInView="visible"
        viewport={{ once: true, amount: 0.05 }}
        variants={{ visible: { transition: { staggerChildren: 0.07 } } }}
      >
        {servicesContent.services.map((service) => (
          <motion.li
            key={service.id}
            id={service.id}
            variants={cardVariants}
            className="brand-card scroll-mt-24 p-6 lg:p-8"
          >
            <CertificationLogos
              logoKeys={[service.logoKey]}
              label={`${service.title} certification mark`}
              className="flex h-[4.5rem] items-center"
              size="h-[4.5rem]"
            />

            <h3 className="mt-4 text-lg lg:text-xl font-bold text-gray-900">
              {service.title}
            </h3>
            <p className="mt-1 text-sm md:text-base font-semibold text-[var(--brand-primary)]">
              {service.headline}
            </p>

            <div className="mt-4 space-y-3">
              {service.paragraphs.map((paragraph) => (
                <p
                  key={paragraph}
                  className="text-sm md:text-base leading-relaxed text-gray-600"
                >
                  {paragraph}
                </p>
              ))}
            </div>

            <p className="mt-5 text-sm font-semibold text-gray-900">
              {service.listLabel}
            </p>
            <ul className="mt-3 grid grid-cols-1 gap-x-6 gap-y-2 sm:grid-cols-2">
              {service.items.map((item) => (
                <li key={item} className="flex items-start gap-3">
                  <FiCheckCircle
                    aria-hidden="true"
                    size={18}
                    className="mt-0.5 flex-shrink-0 text-[var(--brand-primary)]"
                  />
                  <span className="text-sm md:text-base text-gray-700">
                    {item}
                  </span>
                </li>
              ))}
            </ul>

            {/* mt-auto pins the benefits panel to the bottom of the card, so
                the panels line up across a row whatever each list runs to */}
            <div className="mt-auto pt-6">
              <div className="rounded-xl bg-[rgba(104,46,146,0.05)] p-4 sm:p-5">
                <p className="text-sm font-semibold text-gray-900">
                  {service.benefitsLabel}
                </p>
                <ul className="mt-3 space-y-2">
                  {service.benefits.map((benefit) => (
                    <li key={benefit} className="flex items-start gap-3">
                      <span
                        aria-hidden="true"
                        className="mt-2 h-1.5 w-1.5 flex-shrink-0 rounded-full bg-[var(--brand-orange)]"
                      />
                      <span className="text-sm md:text-base text-gray-700">
                        {benefit}
                      </span>
                    </li>
                  ))}
                </ul>
              </div>
            </div>
          </motion.li>
        ))}
      </motion.ul>
    </div>
  </section>
);

export default FoodSafetyStandards;
